import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { FROM_CLIENTVERSE_HINT, FROM_CLIENTVERSE_LABEL, fromClientverseChip } from '../modules/data-mapping/fromClientverse';
import {
  CvObjectFieldGroup, INTERNAL_OVERVIEW, InternalNote, OVERVIEW, OpenQuestion, QuestionStatus, SOURCES, STEPS, SourceCode, Step,
  StagingTableGroup, allQuestions, buildApiExamples, landingToMarkdown, questionsToMarkdown, shortTitle, stepBySlug, stepToMarkdown,
} from './systemToClientverseContent';

const BASE_PATH = '/admin/modules/data-mapping/system-to-clientverse';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportMarkdown(filename: string, markdown: string) {
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  triggerDownload(blob, filename);
}

// Landing page — purpose, scope, and a short-description link to each step.
export default function AdminSystemToClientverse() {
  return (
    <div>
      <Breadcrumbs current={OVERVIEW.title} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <p style={{ color: 'var(--mi-muted)', marginTop: 4, maxWidth: 760 }}>
          {OVERVIEW.purpose[0]}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`${BASE_PATH}/open-questions`} style={{ ...outlineBtn, textDecoration: 'none' }}>
            Open questions ({allQuestions().flatMap((g) => g.questions).filter((q) => q.status !== 'Decided').length} unresolved)
          </Link>
          <button
            onClick={() => exportMarkdown('system-to-clientverse-load-plan.md', landingToMarkdown())}
            style={outlineBtn}
          >
            Export to MD
          </button>
        </div>
      </div>

      {OVERVIEW.purpose.slice(1).map((p, i) => (
        <p key={i} style={{ color: 'var(--mi-muted)', fontSize: 14 }}>{p}</p>
      ))}

      <div style={noteBox}>
        <strong>Scope note.</strong> {OVERVIEW.scopeNote}
      </div>

      <h2 style={sectionHeading}>Steps</h2>
      <section className="card">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 90 }}>Step</th>
              <th style={th}>Name</th>
              <th style={th}>What you’ll see after it lands</th>
              <th style={{ ...th, width: 110 }} title="Internal: MVP scope, TBD until the de-scoping plan is final">MVP</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {STEPS.map((s) => (
              <tr key={s.slug} style={{ borderTop: '1px solid var(--mi-border)' }}>
                <td style={td}>{s.number}</td>
                <td style={{ ...td, fontWeight: 600, color: 'var(--mi-navy)' }}>
                  <Link to={`${BASE_PATH}/${s.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {shortTitle(s)}
                  </Link>
                  {s.internalOnly && <span style={{ ...internalTag, marginLeft: 8 }}>Internal</span>}
                </td>
                <td style={{ ...td, color: 'var(--mi-muted)' }}>{s.shortDescription}</td>
                <td style={td}>{s.mvp && <span style={mvpChip(s.mvp)}>{s.mvp}</span>}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <Link to={`${BASE_PATH}/${s.slug}`} style={{ ...outlineBtn, textDecoration: 'none', whiteSpace: 'nowrap' }}>View →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <h2 style={sectionHeading}>Notes & caveats</h2>
      <ul style={{ color: 'var(--mi-muted)', fontSize: 14, paddingLeft: 20 }}>
        {OVERVIEW.notesAndCaveats.map((n, i) => <li key={i} style={{ marginBottom: 8 }}>{n}</li>)}
      </ul>

      <div style={internalDivider}>Internal — not shared with the client</div>
      <SectionHeading>Cross-cutting context</SectionHeading>
      <NoteList notes={INTERNAL_OVERVIEW.context} />

      <SectionHeading>Integrations outside this plan</SectionHeading>
      <NoteList notes={INTERNAL_OVERVIEW.outsideThisPlan.items} />
      <p style={{ fontSize: 14, color: 'var(--mi-muted)' }}>{INTERNAL_OVERVIEW.outsideThisPlan.watch}</p>
    </div>
  );
}

// Every question across the plan on one page: the agenda for a session with
// the ClientVerse architect. Answers are recorded in systemToClientverseContent.ts.
export function AdminSystemToClientverseQuestions() {
  const [filter, setFilter] = useState<QuestionStatus | 'All' | 'Unresolved'>('Unresolved');
  const groups = allQuestions();
  const all = groups.flatMap((g) => g.questions);
  const count = (s: QuestionStatus) => all.filter((q) => q.status === s).length;
  const keep = (q: OpenQuestion) => filter === 'All' || (filter === 'Unresolved' ? q.status !== 'Decided' : q.status === filter);
  const filters: Array<{ key: typeof filter; label: string }> = [
    { key: 'Unresolved', label: `Unresolved (${count('Open') + count('Partial')})` },
    { key: 'Open', label: `Open (${count('Open')})` },
    { key: 'Partial', label: `Partial (${count('Partial')})` },
    { key: 'Decided', label: `Decided — to confirm (${count('Decided')})` },
    { key: 'All', label: `All (${all.length})` },
  ];
  return (
    <div>
      <Breadcrumbs current="Open questions" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
        <Link to={BASE_PATH} style={{ ...outlineBtn, textDecoration: 'none' }}>← Back to Steps</Link>
        <button onClick={() => exportMarkdown('clientverse-architect-open-questions.md', questionsToMarkdown())} style={outlineBtn}>
          Export agenda to MD
        </button>
      </div>
      <h1 style={{ color: 'var(--mi-green)', margin: '0 0 4px' }}>Open questions for the ClientVerse architect</h1>
      <p style={{ color: 'var(--mi-muted)', fontSize: 14, marginTop: 0, maxWidth: 820 }}>
        Internal. Grouped by the page each question is about; IDs are stable, so they can be used in Asana and agendas.
        Record an answer on the question, then move the decision into its step. Every question comes from a meeting summary
        or transcript: spot-check it against the recording before anything goes to a client.
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {filters.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ ...outlineBtn, ...(filter === f.key ? activeFilter : undefined) }}>{f.label}</button>
        ))}
      </div>
      {groups.map(({ step, questions }) => {
        const shown = questions.filter(keep);
        if (shown.length === 0) return null;
        return (
          <section key={step.slug} style={{ marginBottom: 20 }}>
            <h2 style={{ ...sectionHeading, marginBottom: 8 }}>
              <Link to={`${BASE_PATH}/${step.slug}`} style={{ color: 'inherit', textDecoration: 'none' }}>{step.title}</Link>
              {step.internalOnly && <span style={{ ...internalTag, marginLeft: 8 }}>Internal</span>}
            </h2>
            <QuestionList questions={shown} />
          </section>
        );
      })}
      <SectionHeading>Source key</SectionHeading>
      <table style={plainTable}>
        <thead><tr><th style={{ ...th, width: 80 }}>Code</th><th style={th}>Meeting</th><th style={{ ...th, width: 100 }}>Date</th></tr></thead>
        <tbody>
          {(Object.keys(SOURCES) as SourceCode[]).map((code) => (
            <tr key={code} style={{ borderTop: '1px solid var(--mi-border)' }}>
              <td style={{ ...td, fontFamily: 'monospace' }}>{code}</td>
              <td style={td}>{SOURCES[code].url ? <a href={SOURCES[code].url!} target="_blank" rel="noreferrer">{SOURCES[code].meeting}</a> : SOURCES[code].meeting}</td>
              <td style={td}>{SOURCES[code].date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceLinks({ sources }: { sources?: readonly SourceCode[] }) {
  if (!sources || sources.length === 0) return null;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {sources.map((code) => {
        const s = SOURCES[code];
        const title = `${s.meeting}${s.date ? ` — ${s.date}` : ''}`;
        return s.url
          ? <a key={code} href={s.url} target="_blank" rel="noreferrer" title={title} style={sourceChip}>{code}</a>
          : <span key={code} title={title} style={sourceChip}>{code}</span>;
      })}
    </span>
  );
}

function NoteList({ notes }: { notes: readonly InternalNote[] }) {
  return (
    <ul style={{ fontSize: 14, paddingLeft: 20, margin: '0 0 16px' }}>
      {notes.map((n, i) => (
        <li key={i} style={{ marginBottom: 6 }}>
          {n.text} <SourceLinks sources={n.sources} />
        </li>
      ))}
    </ul>
  );
}

function QuestionList({ questions }: { questions: readonly OpenQuestion[] }) {
  return (
    <div style={openQuestionsBox}>
      {questions.map((q, i) => (
        <div key={q.id} style={{ paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? '1px solid #f0dca3' : undefined }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={questionId}>{q.id}</span>
            <span style={statusChip(q.status)}>{q.status === 'Decided' ? 'Decided — to confirm' : q.status}</span>
            <SourceLinks sources={q.sources} />
          </div>
          <div style={{ marginTop: 4 }}>{q.question}</div>
          {q.answer && <div style={answerBox}><strong>So far: </strong>{q.answer}</div>}
        </div>
      ))}
    </div>
  );
}

// Per-step detail page.
export function AdminSystemToClientverseStep() {
  const { slug } = useParams<{ slug: string }>();
  const step = stepBySlug(slug);

  if (!step) {
    return (
      <div>
        <Breadcrumbs current="Not found" />
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No step found with that id.</div>
      </div>
    );
  }

  const idx = STEPS.findIndex((s) => s.slug === step.slug);
  const prev = idx > 0 ? STEPS[idx - 1] : null;
  const next = idx < STEPS.length - 1 ? STEPS[idx + 1] : null;

  return (
    <div>
      <Breadcrumbs current={shortTitle(step)} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to={BASE_PATH} style={{ ...outlineBtn, textDecoration: 'none' }}>← Back to Steps</Link>
          {step.mvp && <span style={mvpChip(step.mvp)} title="Internal: MVP scope">MVP: {step.mvp}</span>}
          {step.internalOnly && <span style={internalTag}>Internal page — not for clients</span>}
        </div>
        <button
          onClick={() => exportMarkdown(`${step.slug}-${slugify(step.title)}.md`, stepToMarkdown(step))}
          style={outlineBtn}
        >
          Export to MD
        </button>
      </div>

      <StepSection step={step} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        {prev ? (
          <Link to={`${BASE_PATH}/${prev.slug}`} style={{ ...outlineBtn, textDecoration: 'none' }}>
            ← {shortTitle(prev)}
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`${BASE_PATH}/${next.slug}`} style={{ ...outlineBtn, textDecoration: 'none' }}>
            {shortTitle(next)} →
          </Link>
        ) : <span />}
      </div>
    </div>
  );
}

// Illustrative Salesforce REST call(s) for one object group, generated from
// its own field/sample data (see buildApiExamples) so they can't drift out
// of sync with the table they sit under. Renders nothing for note-only
// groups. Most objects get exactly one call; an object loaded via more than
// one source (e.g. Account) gets one labeled call per source.
function ApiExampleBlock({ group }: { group: CvObjectFieldGroup }) {
  const examples = buildApiExamples(group);
  if (examples.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginBottom: 4 }}>
        Sample API call{examples.length > 1 ? 's' : ''} <span style={{ fontStyle: 'italic' }}>(illustrative — built from the fields above, not a captured request)</span>
      </div>
      {examples.map(({ label, example }, i) => (
        <div key={i} style={{ marginBottom: i < examples.length - 1 ? 10 : 0 }}>
          {label && <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginBottom: 4 }}>{label}</div>}
          <pre style={codeBlock}>
            {[
              `${example.method} ${example.path}`,
              'Content-Type: application/json',
              'Authorization: Bearer <access_token>',
              '',
              JSON.stringify(example.body, null, 2),
            ].join('\n')}
          </pre>
        </div>
      ))}
    </div>
  );
}

// One staging table's populate-instructions, distinct from a Scope row: a
// 'client' table gets a field-by-field instruction table telling the client
// exactly what to hand over; a 'pipeline' table (no client fields) just gets
// a note that there's nothing for the client to supply.
function StagingTableBlock({ group }: { group: StagingTableGroup }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: 'var(--mi-navy)' }}>
          {group.table}
        </span>
        <span style={group.populatedBy === 'client' ? clientInputTag : pipelineOutputTag}>
          {group.populatedBy === 'client' ? 'You supply this' : 'Not populated by you'}
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 8 }}>{group.description}</div>
      {group.note && (
        <div style={{ fontSize: 13, color: 'var(--mi-muted)', fontStyle: 'italic' }}>{group.note}</div>
      )}
      {group.fields && group.fields.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '18%' }}>Field</th>
              <th style={{ ...th, width: 90 }}>Required</th>
              <th style={{ ...th, width: '16%' }}>Sample</th>
              <th style={th}>Instruction</th>
            </tr>
          </thead>
          <tbody>
            {group.fields.map((row, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--mi-border)', ...(row.placeholder ? placeholderRow : undefined) }}>
                <td style={{ ...td, wordBreak: 'break-word' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.field}</div>
                  {row.dataType && <div style={typeChip}>{row.dataType}</div>}
                </td>
                <td style={td}>{row.required ? <span style={requiredTag}>Required</span> : ''}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: row.sample ? 'inherit' : 'var(--mi-muted)', wordBreak: 'break-word' }}>{row.sample ?? '—'}</td>
                <td style={td}>
                  {row.placeholder && <div style={placeholderTag}>Unconfirmed</div>}
                  {row.fromClientverse && <div style={{ ...fromClientverseChip, marginBottom: 4 }} title={FROM_CLIENTVERSE_HINT}>{FROM_CLIENTVERSE_LABEL} — leave empty</div>}
                  {row.instruction}
                  {row.mustMatchPicklist && row.mustMatchPicklist.values.length > 0 && (
                    <div style={picklistNote}>
                      {row.mustMatchPicklist.extensible
                        ? 'One value per row. Example values — not a closed list: '
                        : row.mustMatchPicklist.kind === 'multipicklist'
                        ?'ClientVerse target is a multipicklist — value(s) must match one or more of, verbatim: '
                        : 'ClientVerse target is a picklist — value must match exactly one of, verbatim: '}
                      {row.mustMatchPicklist.values.map((v, vi) => (
                        <code key={vi} style={picklistValueChip}>{v}</code>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StepSection({ step }: { step: Step }) {
  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-purple)' }}>
      {step.objective.map((p, i) => (
        <p key={i} style={{ marginTop: i === 0 ? 0 : undefined }}>
          {i === 0 && <strong>Objective. </strong>}
          {p}
        </p>
      ))}

      {step.sourceRows && step.sourceRows.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Scope</SectionHeading>
          {step.sourceRowsCaption && (
            <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--mi-muted)', marginBottom: 6 }}>
              {step.sourceRowsCaption}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Source</th>
                <th style={th}>Lands as (ClientVerse Core)</th>
                <th style={th}>Role</th>
              </tr>
            </thead>
            <tbody>
              {step.sourceRows.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{row.source}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{row.landsAs}</td>
                  <td style={td}>{row.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {step.systemsInvolved && step.systemsInvolved.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Systems involved</SectionHeading>
          <ul style={{ fontSize: 14, paddingLeft: 20, margin: 0 }}>
            {step.systemsInvolved.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
          </ul>
        </div>
      )}

      {step.stagingTables && step.stagingTables.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>{step.stagingTablesHeading ?? 'Staging tables to populate'}</SectionHeading>
          {step.stagingTablesCaption && (
            <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 12 }}>{step.stagingTablesCaption}</div>
          )}
          {step.stagingProcess && (
            <div style={{ fontSize: 14, marginBottom: 18 }}>
              {(step.stagingProcess.intro ?? []).map((p, i) => <p key={i} style={{ margin: '0 0 8px' }}>{p}</p>)}
              <ol style={{ paddingLeft: 20, margin: '0 0 8px' }}>
                {step.stagingProcess.stages.map((s, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <strong>{s.title}.</strong> {s.detail}
                  </li>
                ))}
              </ol>
              {(step.stagingProcess.outro ?? []).map((p, i) => <p key={i} style={{ margin: '0 0 8px' }}>{p}</p>)}
            </div>
          )}
          {step.stagingTables.map((group, gi) => (
            <StagingTableBlock key={gi} group={group} />
          ))}
        </div>
      )}

      {step.cvFieldsToPopulate && step.cvFieldsToPopulate.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>ClientVerse Core fields</SectionHeading>
          {step.cvFieldsCaption && (
            <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 6 }}>{step.cvFieldsCaption}</div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Field</th>
                <th style={th}>Label</th>
                <th style={th}>Role</th>
              </tr>
            </thead>
            <tbody>
              {step.cvFieldsToPopulate.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{row.field}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.label}</td>
                  <td style={td}>{row.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {step.cvObjectFields && step.cvObjectFields.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>ClientVerse Core fields, by object</SectionHeading>
          {step.cvObjectFieldsCaption && (
            <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 12 }}>{step.cvObjectFieldsCaption}</div>
          )}
          {step.cvObjectFields.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--mi-navy)' }}>
                  {group.object}
                </span>
                {!group.note && <span style={cvObjectTag}>ClientVerse Object</span>}
              </div>
              {group.note ? (
                <div style={{ fontSize: 13, color: 'var(--mi-muted)', fontStyle: 'italic' }}>{group.note}</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '37%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={th}>Field</th>
                      <th style={th}>Label</th>
                      <th style={th}>Required</th>
                      <th style={th}>Sample value</th>
                      <th style={th}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(group.fields ?? []).map((row, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--mi-border)' }}>
                        <td style={{ ...td, wordBreak: 'break-word' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.field}</div>
                          {row.dataType && <div style={typeChip}>{row.dataType}</div>}
                        </td>
                        <td style={{ ...td, wordBreak: 'break-word' }}>{row.label}</td>
                        <td style={td}>{row.required ? <span style={requiredTag}>Required</span> : ''}</td>
                        <td style={{ ...td, color: row.sample ? 'inherit' : 'var(--mi-muted)', wordBreak: 'break-word' }}>{row.sample ?? '—'}</td>
                        <td style={td}>{row.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <ApiExampleBlock group={group} />
            </div>
          ))}
        </div>
      )}

      {(step.notes ?? []).map((n, i) => (
        <p key={i} style={{ fontSize: 14, color: 'var(--mi-muted)' }}>{n}</p>
      ))}

      {step.dependsOn && (
        <p style={{ fontSize: 14 }}><strong>Depends on.</strong> {step.dependsOn}</p>
      )}

      {step.demoable.length > 0 && (
        <div>
          <SectionHeading>Demoable in ClientVerse</SectionHeading>
          <ul style={{ fontSize: 14, paddingLeft: 20, margin: 0 }}>
            {step.demoable.map((d, i) => <li key={i} style={{ marginBottom: 6 }}>{d}</li>)}
          </ul>
        </div>
      )}

      {/* Internal working notes — last on the page, and nothing above refers
          to them, so the client-facing part of a step reads on its own. */}
      {((step.openQuestions?.length ?? 0) > 0 || (step.decisions?.length ?? 0) > 0 || (step.internalNotes?.length ?? 0) > 0) && (
        <div style={internalDivider}>Internal — not shared with the client</div>
      )}

      {(step.internalNotes?.length ?? 0) > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Internal notes</SectionHeading>
          <div style={statusBox}>
            <NoteList notes={step.internalNotes!} />
          </div>
        </div>
      )}

      {step.openQuestions && step.openQuestions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeading>Open questions for the ClientVerse architect</SectionHeading>
          <QuestionList questions={step.openQuestions} />
        </div>
      )}

      {step.decisions && step.decisions.length > 0 && (
        <div>
          <SectionHeading>Decisions</SectionHeading>
          <div style={decisionsBox}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {step.decisions.map((d, i) => (
                <li key={i} style={{ marginBottom: i < step.decisions!.length - 1 ? 8 : 0 }}>
                  <span style={decisionDate}>{d.date}</span>
                  {d.decision} <SourceLinks sources={d.sources} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

// Larger section heading with a horizontal rule above it, separating each
// named section (Scope, Systems involved, ClientVerse Core fields, ...,
// Demoable in ClientVerse) from whatever precedes it on the step page.
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <>
      <hr style={sectionRule} />
      <h2 style={sectionTitle}>{children}</h2>
    </>
  );
}

function Breadcrumbs({ current }: { current: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <Link to={BASE_PATH} style={breadcrumb}>System to ClientVerse Core</Link>
      <span style={{ color: 'var(--mi-muted)' }}>›</span>
      <h1 style={{ color: 'var(--mi-navy)', margin: 0, fontSize: 20 }}>{current}</h1>
    </div>
  );
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const sectionHeading: React.CSSProperties = { color: 'var(--mi-navy)', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 24, marginBottom: 12 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--mi-navy)', textTransform: 'uppercase', letterSpacing: 0.5,
  borderBottom: '1px solid var(--mi-border)', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
const outlineBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)',
  color: 'var(--mi-navy)', padding: '6px 12px', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, fontWeight: 500,
};
const noteBox: React.CSSProperties = {
  background: 'var(--mi-bg-alt, #f6f7fb)', border: '1px solid var(--mi-border)',
  borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--mi-muted)',
  margin: '12px 0',
};
const requiredTag: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#a11414',
  background: '#fdecec', border: '1px solid #f3c0c0', borderRadius: 999, padding: '2px 8px',
  whiteSpace: 'nowrap',
};
// Matches the "ClientVerse Object" callout the user asked for next to the
// larger object heading on each per-object field breakdown.
const codeBlock: React.CSSProperties = {
  background: '#0f1729', color: '#d7e2f7', fontFamily: 'monospace', fontSize: 12,
  padding: '12px 14px', borderRadius: 8, overflowX: 'auto', margin: 0, lineHeight: 1.5,
  whiteSpace: 'pre',
};
const sectionRule: React.CSSProperties = {
  border: 'none', borderTop: '1px solid var(--mi-border)', margin: '24px 0 16px',
};
const sectionTitle: React.CSSProperties = {
  fontSize: 18, fontWeight: 700, color: 'var(--mi-navy)', margin: '0 0 10px',
};
const cvObjectTag: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: '#5b3a8e',
  background: '#f0e9fb', border: '1px solid #d9c5f0', borderRadius: 999, padding: '2px 10px',
  whiteSpace: 'nowrap',
};
// Staging-table badges: 'client' fields need action from the client, so the
// tag reads as a to-do (green); 'pipeline' output is informational only
// (muted grey) — deliberately distinct from cvObjectTag's purple so the two
// kinds of table are never visually confused.
const clientInputTag: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: '#1a6b3c',
  background: '#e7f6ee', border: '1px solid #bfe6d1', borderRadius: 999, padding: '2px 10px',
  whiteSpace: 'nowrap',
};
const pipelineOutputTag: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: 'var(--mi-muted)',
  background: 'var(--mi-bg-alt, #f6f7fb)', border: '1px solid var(--mi-border)', borderRadius: 999, padding: '2px 10px',
  whiteSpace: 'nowrap',
};
// Called out in amber, not muted grey, because getting this wrong doesn't
// just leave a field blank — it silently fails to match the picklist and
// the value drops on the floor at load time. Deliberately louder than the
// instruction text above it.
const picklistNote: React.CSSProperties = {
  marginTop: 6, fontSize: 12, color: '#8a5a00', background: '#fff8e6',
  border: '1px solid #f0dca3', borderRadius: 6, padding: '6px 8px', lineHeight: 1.6,
};
const picklistValueChip: React.CSSProperties = {
  display: 'inline-block', margin: '2px 4px 2px 0', padding: '1px 6px', fontSize: 11,
  borderRadius: 4, background: '#ffffff', border: '1px solid #f0dca3', fontFamily: 'monospace',
};
// A placeholder row is a guess, not verified schema — a faint tint on the
// whole row plus a leading tag keeps that visible even if someone skims
// past the per-field note in the Instruction cell.
const placeholderRow: React.CSSProperties = {
  background: '#faf6ee',
};
const placeholderTag: React.CSSProperties = {
  display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
  color: '#8a5a00', background: '#fff2cc', border: '1px solid #f0dca3', borderRadius: 4,
  padding: '1px 6px', marginBottom: 4,
};
const internalTag: React.CSSProperties = {
  display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  color: '#6b4c9a', background: '#f4eef7', border: '1px solid #e0cdee', borderRadius: 4, padding: '1px 6px',
};
const mvpChip = (mvp: string): React.CSSProperties => ({
  display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
  ...(mvp === 'Required' ? { background: '#eefaf1', color: '#2f6b40', border: '1px solid #bfe3c9' }
    : mvp === 'TBD' ? { background: '#f4f5f7', color: 'var(--mi-muted)', border: '1px solid var(--mi-border)' }
    : { background: '#fff8e6', color: '#8a5a00', border: '1px solid #f0dca3' }),
});
const statusChip = (status: QuestionStatus): React.CSSProperties => ({
  display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '1px 6px',
  ...(status === 'Open' ? { background: '#fff2cc', color: '#8a5a00', border: '1px solid #f0dca3' }
    : status === 'Partial' ? { background: '#eef2ff', color: '#3b4ea1', border: '1px solid #d6deff' }
    : { background: '#eefaf1', color: '#2f6b40', border: '1px solid #bfe3c9' }),
});
const questionId: React.CSSProperties = { fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: 'var(--mi-navy)' };
const sourceChip: React.CSSProperties = {
  display: 'inline-block', marginRight: 4, padding: '0 5px', fontSize: 10, fontFamily: 'monospace', borderRadius: 4,
  background: '#ffffff', border: '1px solid var(--mi-border)', color: 'var(--mi-muted)', textDecoration: 'none',
};
const answerBox: React.CSSProperties = {
  marginTop: 6, padding: '6px 8px', fontSize: 13, background: '#ffffff', border: '1px solid #f0dca3', borderRadius: 6,
};
const statusBox: React.CSSProperties = {
  background: '#f6f7fb', border: '1px solid var(--mi-border)', borderRadius: 8, padding: '12px 16px 0',
};
const plainTable: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' };
const activeFilter: React.CSSProperties = { background: 'var(--mi-navy)', color: '#fff', borderColor: 'var(--mi-navy)' };
const openQuestionsBox: React.CSSProperties = {
  background: '#fff8e6', border: '1px solid #f0dca3', borderRadius: 8,
  padding: '12px 16px', fontSize: 14,
};
const internalDivider: React.CSSProperties = {
  marginTop: 28, marginBottom: 12, paddingTop: 12, borderTop: '2px dashed var(--mi-border)',
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--mi-muted)',
};
// Settled, so green rather than the open-questions amber — the two boxes sit
// next to each other and should read as "still open" vs "done".
const decisionsBox: React.CSSProperties = {
  background: '#eefaf1', border: '1px solid #bfe3c9', borderRadius: 8,
  padding: '12px 16px', fontSize: 14,
};
const decisionDate: React.CSSProperties = {
  display: 'inline-block', marginRight: 8, padding: '1px 6px', fontSize: 11, fontWeight: 700,
  borderRadius: 4, background: '#ffffff', border: '1px solid #bfe3c9', color: '#2f6b40', fontFamily: 'monospace',
};
// Field data type, shown under the field name — same chip convention as
// AdminClientverseCoreBrowser's typeChip, raw sf_fields.data_type value.
const typeChip: React.CSSProperties = {
  display: 'inline-block', marginTop: 3, padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#eef2ff', color: '#3b4ea1', border: '1px solid #d6deff', fontFamily: 'monospace', whiteSpace: 'nowrap',
};
